import React, { useMemo, useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { SunIcon, CloudIcon } from '@heroicons/react/24/outline';
import { smartFarmCockpitApi } from '../../../api/smartFarmCockpit';

function toNumOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function extractStationHumidity(payload) {
  if (!payload || payload.deviceBound === false) return null;
  const fw = payload.fieldWeather || {};
  const candidates = [
    fw.rh,
    fw.humidity,
    fw.airRh,
    fw.air_rh,
    payload.rh,
    payload.humidity,
  ];
  for (const c of candidates) {
    const n = toNumOrNull(c);
    if (n != null) return Math.round(n);
  }
  // 近 24h 趋势：取最近有效湿度点
  const trend = Array.isArray(payload.trend24h) ? payload.trend24h : [];
  for (let i = trend.length - 1; i >= 0; i -= 1) {
    const n = toNumOrNull(trend[i]?.rh ?? trend[i]?.humidity);
    if (n != null) return Math.round(n);
  }
  return null;
}

const METRIC_MODES = [
  {
    key: 'temp',
    label: '温度',
    lines: [
      { dataKey: 'highTemp', name: '最高温度', stroke: '#ef4444', unit: '°C', labelPos: 'top' },
      { dataKey: 'lowTemp', name: '最低温度', stroke: '#3b82f6', unit: '°C', labelPos: 'bottom' },
    ],
  },
  {
    key: 'humidity',
    label: '湿度',
    lines: [
      { dataKey: 'humidity', name: '湿度', stroke: '#22c55e', unit: '%', labelPos: 'top' },
    ],
  },
  {
    key: 'wind',
    label: '风力',
    lines: [
      { dataKey: 'wind', name: '风力', stroke: '#a855f7', unit: '级', labelPos: 'top' },
    ],
  },
];

const WeatherChart = React.memo(function WeatherChart({
  weather,
  forecast,
  loading,
}) {
  const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  const [modeIndex, setModeIndex] = useState(0);
  const [stationHumidity, setStationHumidity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadStation = async () => {
      try {
        // 气象站为租户级共享，不传 farmId 避免农场缺失/跨租户导致 400
        const res = await smartFarmCockpitApi.getFieldWeather();
        const body = res?.data?.data ?? res?.data ?? null;
        if (cancelled) return;
        setStationHumidity(extractStationHumidity(body));
      } catch {
        if (!cancelled) setStationHumidity(null);
      }
    };
    loadStation();
    return () => {
      cancelled = true;
    };
  }, []);

  const forecastData = useMemo(() => {
    const bannerHumidity = toNumOrNull(weather?.humidity);
    const stationRh = toNumOrNull(stationHumidity) ?? bannerHumidity;

    if (forecast && forecast.length > 0) {
      return forecast.slice(0, 7).map((day, i) => {
        const date = new Date();
        date.setDate(date.getDate() + i);
        const forecastHumidity =
          toNumOrNull(day.humidity) ??
          toNumOrNull(day.humidityAvg) ??
          toNumOrNull(day.humidity_avg) ??
          toNumOrNull(day.rh);
        // 今日优先气象站实况湿度；其余日用预报湿度，仍缺时回退气象站
        const humidity =
          i === 0
            ? (stationRh ?? forecastHumidity)
            : (forecastHumidity ?? stationRh);
        return {
          date: `${date.getMonth() + 1}/${date.getDate()}`,
          weekday: i === 0 ? '今天' : weekDays[date.getDay()],
          highTemp: toNumOrNull(day.highTemp ?? day.tempMax),
          lowTemp: toNumOrNull(day.lowTemp ?? day.tempMin),
          humidity: humidity != null ? Math.round(humidity) : null,
          wind: toNumOrNull(day.windSpeed ?? day.windScale ?? day.wind),
          condition: day.condition || day.text || '',
        };
      });
    }

    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      return {
        date: `${date.getMonth() + 1}/${date.getDate()}`,
        weekday: i === 0 ? '今天' : weekDays[date.getDay()],
        highTemp: i === 0 ? toNumOrNull(weather?.tempMax) : null,
        lowTemp: i === 0 ? toNumOrNull(weather?.tempMin) : null,
        humidity: i === 0 && stationRh != null ? Math.round(stationRh) : null,
        wind: i === 0 ? toNumOrNull(weather?.windSpeed) : null,
        condition: i === 0 ? (weather?.text || '') : '',
      };
    });
  }, [forecast, weather, stationHumidity]);

  const mode = METRIC_MODES[modeIndex] || METRIC_MODES[0];

  const getConditionIcon = (condition) => {
    if (condition?.includes('晴') && !condition?.includes('多云')) {
      return <SunIcon className="h-5 w-5 text-amber-500 mx-auto" aria-hidden />;
    }
    if (condition?.includes('雨')) {
      return <CloudIcon className="h-5 w-5 text-sky-500 mx-auto" aria-hidden />;
    }
    if (condition?.includes('阴')) {
      return <CloudIcon className="h-5 w-5 text-gray-400 mx-auto" aria-hidden />;
    }
    return <CloudIcon className="h-5 w-5 text-gray-500 mx-auto" aria-hidden />;
  };

  const formatHumidity = (v) => (v == null ? '--' : `${v}%`);
  const formatWind = (v) => (v == null ? '--' : `${v}级`);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <CloudIcon className="h-4 w-4 text-sky-600 shrink-0" aria-hidden />
          <h3 className="text-sm font-semibold text-gray-800">气象趋势</h3>
          {stationHumidity != null ? (
            <span className="text-xs text-gray-600 truncate hidden sm:inline">
              湿度来自气象站实况
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-sm shrink-0">
          {METRIC_MODES.map((m, i) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setModeIndex(i)}
              className={`px-2.5 py-1 rounded border transition-colors ${
                i === modeIndex
                  ? 'bg-green-50 border-green-300 text-green-800 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-7 gap-1 mb-4">
          {forecastData.map((day, i) => (
            <div key={i} className={`text-center py-2 rounded-lg ${i === 0 ? 'bg-green-50 border border-green-200' : ''}`}>
              <div className="text-xs text-gray-600">{day.weekday}</div>
              <div className="text-sm text-gray-700 font-medium">{day.date}</div>
              <div className="my-1 flex justify-center">
                {day.condition ? getConditionIcon(day.condition) : <span className="text-xs text-gray-400 leading-5">--</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="h-[180px] w-full">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin h-6 w-6 border-2 border-green-200 border-t-green-600 rounded-full" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={forecastData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#4B5563' }} axisLine={{ stroke: '#eee' }} />
                <YAxis tick={{ fontSize: 12, fill: '#4B5563' }} axisLine={{ stroke: '#eee' }} width={30} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }}
                  formatter={(value, name) => {
                    const line = mode.lines.find((l) => l.name === name || l.dataKey === name);
                    const unit = line?.unit || '';
                    const label = line?.name || name;
                    if (value == null) return ['--', label];
                    return [`${value}${unit}`, label];
                  }}
                />
                {mode.lines.map((line) => (
                  <Line
                    key={line.dataKey}
                    type="monotone"
                    dataKey={line.dataKey}
                    name={line.name}
                    stroke={line.stroke}
                    strokeWidth={2.5}
                    dot={{ fill: line.stroke, r: 4, strokeWidth: 2, stroke: '#fff' }}
                    label={{ position: line.labelPos, fontSize: 12, fill: line.stroke, fontWeight: 600 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="grid grid-cols-7 gap-1 mt-3 pt-3 border-t border-gray-100">
          {forecastData.map((day, i) => (
            <div key={i} className="text-center">
              <div className="text-xs text-gray-600">湿度</div>
              <div className="text-sm text-green-700 font-medium">{formatHumidity(day.humidity)}</div>
              <div className="text-xs text-gray-600 mt-1">风力</div>
              <div className="text-sm text-purple-700 font-medium">{formatWind(day.wind)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default WeatherChart;
